import { useState } from 'react';
import { BLOG_POSTS, BlogPost } from '../../data/staticContent';

// Helper functions for local storage
const STORAGE_KEY = 'admin_blogs';

function getStoredBlogs(): BlogPost[] {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) return JSON.parse(stored);
  } catch {}
  return [...BLOG_POSTS];
}

function saveBlogs(blogs: BlogPost[]) {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(blogs));
}

export function BlogsTab() {
  const [blogs, setBlogs] = useState<BlogPost[]>(getStoredBlogs);
  const [searchTerm, setSearchTerm] = useState('');
  const [isEditing, setIsEditing] = useState(false);
  const [currentBlog, setCurrentBlog] = useState<Partial<BlogPost>>({});

  const filteredBlogs = blogs.filter(blog => 
    blog.titleSr.toLowerCase().includes(searchTerm.toLowerCase()) ||
    blog.titleEn.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleSave = () => {
    if (currentBlog.id) {
      // Update existing
      const updatedBlogs = blogs.map(b => b.id === currentBlog.id ? { ...b, ...currentBlog } as BlogPost : b);
      setBlogs(updatedBlogs);
      saveBlogs(updatedBlogs);
    } else {
      // Create new
      const newBlog: BlogPost = {
        id: Date.now().toString(),
        date: new Date().toISOString().split('T')[0],
        ...currentBlog as any
      };
      const updatedBlogs = [newBlog, ...blogs];
      setBlogs(updatedBlogs);
      saveBlogs(updatedBlogs);
    }
    setIsEditing(false);
    setCurrentBlog({});
  };

  const handleDelete = (id: string) => {
    if (confirm('Da li ste sigurni da želite da obrišete ovaj blog?')) {
      const updatedBlogs = blogs.filter(b => b.id !== id);
      setBlogs(updatedBlogs);
      saveBlogs(updatedBlogs);
    }
  };

  const handleEdit = (blog: BlogPost) => {
    setCurrentBlog(blog);
    setIsEditing(true);
  };

  const handleNew = () => {
    setCurrentBlog({
      titleSr: '',
      titleEn: '',
      excerptSr: '',
      excerptEn: '',
      contentSr: '',
      contentEn: '',
      author: 'Admin',
      category: '',
      image: '',
    });
    setIsEditing(true);
  };

  const handleImageUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setCurrentBlog({ ...currentBlog, image: reader.result as string });
      };
      reader.readAsDataURL(file);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header & Filters */}
      <div className="flex flex-wrap items-center justify-between gap-4 bg-white dark:bg-slate-800 p-4 rounded-xl shadow-sm">
        <div className="flex-1 min-w-[200px]">
          <input
            type="text"
            placeholder="Pretraži blogove..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full px-4 py-2 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-900 dark:text-white"
          />
        </div>
        <button
          onClick={handleNew}
          className="px-4 py-2 bg-amber-500 text-white rounded-lg hover:bg-amber-600 transition-colors flex items-center gap-2"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
          </svg>
          Novi Blog
        </button>
      </div>

      {/* Blogs Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {filteredBlogs.map((blog) => (
          <div key={blog.id} className="bg-white dark:bg-slate-800 rounded-xl shadow-sm overflow-hidden flex flex-col">
            <div className="h-48 bg-slate-200 dark:bg-slate-700 relative">
              {blog.image ? (
                <img src={blog.image} alt={blog.titleSr} className="w-full h-full object-cover" />
              ) : (
                <div className="flex items-center justify-center h-full text-slate-400">
                  <svg className="w-12 h-12" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                </div>
              )}
              <div className="absolute top-2 right-2 flex gap-2">
                <button
                  onClick={() => handleEdit(blog)}
                  className="p-2 bg-white/90 dark:bg-slate-800/90 rounded-lg text-amber-600 hover:text-amber-500 shadow-sm"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                  </svg>
                </button>
                <button
                  onClick={() => handleDelete(blog.id)}
                  className="p-2 bg-white/90 dark:bg-slate-800/90 rounded-lg text-red-600 hover:text-red-500 shadow-sm"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                  </svg>
                </button>
              </div>
            </div>
            <div className="p-4 flex-1 flex flex-col">
              <div className="text-sm text-slate-500 dark:text-slate-400 mb-2">{blog.date} • {blog.category}</div>
              <h3 className="font-bold text-slate-900 dark:text-white mb-2 line-clamp-2">{blog.titleSr}</h3>
              <p className="text-slate-600 dark:text-slate-400 text-sm line-clamp-3 mb-4 flex-1">{blog.excerptSr}</p>
            </div>
          </div>
        ))}
      </div>

      {/* Edit Modal */}
      {isEditing && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white dark:bg-slate-800 rounded-xl shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto p-6">
            <h3 className="text-xl font-bold text-slate-900 dark:text-white mb-6">
              {currentBlog.id ? 'Izmeni blog' : 'Novi blog'}
            </h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Serbian Content */}
              <div className="space-y-4">
                <h4 className="font-semibold text-slate-900 dark:text-white border-b pb-2">Srpski</h4>
                <div>
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Naslov</label>
                  <input
                    type="text"
                    value={currentBlog.titleSr || ''}
                    onChange={(e) => setCurrentBlog({ ...currentBlog, titleSr: e.target.value })}
                    className="w-full px-3 py-2 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-900 dark:text-white"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Kratak opis</label>
                  <textarea
                    value={currentBlog.excerptSr || ''}
                    onChange={(e) => setCurrentBlog({ ...currentBlog, excerptSr: e.target.value })}
                    rows={3}
                    className="w-full px-3 py-2 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-900 dark:text-white"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Sadržaj</label>
                  <textarea
                    value={currentBlog.contentSr || ''}
                    onChange={(e) => setCurrentBlog({ ...currentBlog, contentSr: e.target.value })}
                    rows={10}
                    className="w-full px-3 py-2 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-900 dark:text-white"
                  />
                </div>
              </div>

              {/* English Content */}
              <div className="space-y-4">
                <h4 className="font-semibold text-slate-900 dark:text-white border-b pb-2">English</h4>
                <div>
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Title</label>
                  <input
                    type="text"
                    value={currentBlog.titleEn || ''}
                    onChange={(e) => setCurrentBlog({ ...currentBlog, titleEn: e.target.value })}
                    className="w-full px-3 py-2 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-900 dark:text-white"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Excerpt</label>
                  <textarea
                    value={currentBlog.excerptEn || ''}
                    onChange={(e) => setCurrentBlog({ ...currentBlog, excerptEn: e.target.value })}
                    rows={3}
                    className="w-full px-3 py-2 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-900 dark:text-white"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Content</label>
                  <textarea
                    value={currentBlog.contentEn || ''}
                    onChange={(e) => setCurrentBlog({ ...currentBlog, contentEn: e.target.value })}
                    rows={10}
                    className="w-full px-3 py-2 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-900 dark:text-white"
                  />
                </div>
              </div>
            </div>

            {/* Common Fields */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-6 pt-6 border-t border-slate-200 dark:border-slate-700">
              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Autor</label>
                <input
                  type="text"
                  value={currentBlog.author || ''}
                  onChange={(e) => setCurrentBlog({ ...currentBlog, author: e.target.value })}
                  className="w-full px-3 py-2 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-900 dark:text-white"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Kategorija</label>
                <input
                  type="text"
                  value={currentBlog.category || ''}
                  onChange={(e) => setCurrentBlog({ ...currentBlog, category: e.target.value })}
                  className="w-full px-3 py-2 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-900 dark:text-white"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Slika</label>
                <div className="space-y-2">
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={currentBlog.image || ''}
                      onChange={(e) => setCurrentBlog({ ...currentBlog, image: e.target.value })}
                      placeholder="https://..."
                      className="flex-1 px-3 py-2 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-900 dark:text-white"
                    />
                    <label className="px-4 py-2 bg-slate-100 dark:bg-slate-700 border border-slate-300 dark:border-slate-600 rounded-lg cursor-pointer hover:bg-slate-200 dark:hover:bg-slate-600 transition-colors flex items-center gap-2">
                      <svg className="w-5 h-5 text-slate-600 dark:text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                      </svg>
                      <span>Upload</span>
                      <input
                        type="file"
                        accept="image/*"
                        className="hidden"
                        onChange={(e) => {
                          const file = e.target.files?.[0];
                          if (file) {
                            const reader = new FileReader();
                            reader.onload = (event) => {
                              if (event.target?.result) {
                                setCurrentBlog({ ...currentBlog, image: event.target.result as string });
                              }
                            };
                            reader.readAsDataURL(file);
                          }
                        }}
                      />
                    </label>
                  </div>
                  {currentBlog.image && (
                    <div className="relative w-full h-32 bg-slate-100 dark:bg-slate-900 rounded-lg overflow-hidden">
                      <img src={currentBlog.image} alt="Preview" className="w-full h-full object-cover" />
                      <button
                        onClick={() => setCurrentBlog({ ...currentBlog, image: '' })}
                        className="absolute top-2 right-2 p-1 bg-red-500 text-white rounded-full hover:bg-red-600"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className="flex justify-end gap-3 mt-8">
              <button
                onClick={() => setIsEditing(false)}
                className="px-6 py-2 text-slate-600 hover:text-slate-800 dark:text-slate-400 dark:hover:text-slate-200"
              >
                Otkaži
              </button>
              <button
                onClick={handleSave}
                className="px-6 py-2 bg-amber-500 text-white rounded-lg hover:bg-amber-600 font-medium"
              >
                Sačuvaj
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}